import type { AppMode } from '../contexts/ModeContext';

// First path segment after the owner that unambiguously belongs to Plan mode.
const PLAN_SEGMENTS = new Set([
  'plan',
  'players',
  'reports',
  'projection',
  'ghost',
  'scout',
  'database',
  'planning'
]);

// First path segment after the owner that unambiguously belongs to Season mode.
const SEASON_SEGMENTS = new Set(['active']);

/**
 * Derive the app mode (Plan/Season) implied by a URL pathname.
 *
 * Routes are scoped as `/{owner}/{section}/...`. Mode-specific sections map to a
 * mode; the bare owner dashboard (`/{owner}`) is Season. Sections shared across
 * modes (settings, account, activity, generic models) return `null` so callers
 * keep the user's last chosen ("sticky") mode rather than forcing a switch.
 */
export function deriveModeFromPath(pathname: string): AppMode | null {
  const segments = pathname.split('/').filter(Boolean);

  // No owner segment (e.g. '/', '/auth/...' handled below): nothing to derive.
  if (segments.length === 0) return null;

  const section = segments[1];

  // Bare `/{owner}` is the Season dashboard.
  if (section === undefined) return 'season';

  if (PLAN_SEGMENTS.has(section)) return 'plan';
  if (SEASON_SEGMENTS.has(section)) return 'season';

  // Shared/ambiguous route — let the sticky mode win.
  return null;
}
